import { multilineTrim } from 'helpers/string'

type PresetAlike = Record<string, unknown> & {
  id: number
  title: string
}

type GetRiskWarningTextProps = {
  presets: PresetAlike[]
  desiredPresetIndex: number
  recommendedPresetIndex: number
  isNewQuestionnaire: boolean
  preText?: string
  postText?: string
}

function getRiskWarningText({
  presets,
  desiredPresetIndex,
  recommendedPresetIndex,
  isNewQuestionnaire,
  preText,
  postText,
}: GetRiskWarningTextProps): string {
  if (presets.length < 1) {
    return ''
  }

  const recommendedPreset: PresetAlike | undefined = presets[recommendedPresetIndex]
  const desiredPreset: PresetAlike | undefined = presets[desiredPresetIndex]

  if (!recommendedPreset || !desiredPreset) {
    return ''
  }

  const recommendedPresetTitle = recommendedPreset.title
  const desiredPresetTitle = desiredPreset.title
  const lowerBoundPresetTitle = presets[recommendedPresetIndex - 2]?.title ?? presets.at(0)?.title
  const upperBoundPresetTitle = presets[recommendedPresetIndex + 2]?.title ?? presets.at(-1)?.title
  const isLowerRiskThanRecommended = desiredPresetIndex < recommendedPresetIndex
  const isHigherRiskThanRecommended = desiredPresetIndex > recommendedPresetIndex

  if (isLowerRiskThanRecommended) {
    if (isNewQuestionnaire) {
      return multilineTrim(`
        ${preText ?? ''}

        Based on your responses to our risk tolerance questionnaire, we have determined that our portfolios ${lowerBoundPresetTitle} to ${upperBoundPresetTitle} are suitable for your investment needs. We recommend ${recommendedPresetTitle} as best suited to your needs, however you may proceed with your current selection of ${desiredPresetTitle}.

        While lower-risk investments reduce the likelihood of short-term losses, they may also limit the potential for higher returns, which could mean your investments do not grow at the pace needed to meet your objectives. Please be aware that this portfolio may not provide the level of potential growth required to meet your long-term investment goals.

        ${postText ?? ''}
      `)
    }

    return multilineTrim(`
      ${preText ?? ''}

      You have selected a portfolio that is less risky than the one we suggested based on your answers to the risk questionnaire. Although the current portfolio has lower expected losses you might not be able to achieve your investment objectives.

      ${postText ?? ''}
    `)
  }

  if (isHigherRiskThanRecommended) {
    if (isNewQuestionnaire) {
      return multilineTrim(`
        ${preText ?? ''}

        Based on your responses to our risk tolerance questionnaire, we have determined that our portfolios ${lowerBoundPresetTitle} to ${upperBoundPresetTitle} are suitable for your investment needs. We recommend ${recommendedPresetTitle} as best suited to your needs, however you may proceed with your current selection of ${desiredPresetTitle}.

        While higher-risk portfolios can offer greater potential for returns, they also come with increased uncertainty and the possibility of losing more than you might be comfortable with. Please be aware that this portfolio carries a higher potential for volatility than our recommended portfolio, meaning that your investments may experience larger short-term losses.

        ${postText ?? ''}
      `)
    }

    return multilineTrim(`
      ${preText ?? ''}

      You have selected a portfolio that is more risky than the one we suggested based on your answers to the risk questionnaire. Although the current portfolio has higher expected returns it also has higher potential losses than the optimal portfolio.

      ${postText ?? ''}
    `)
  }

  return multilineTrim(`${preText ?? ''} ${postText ?? ''}`)
}

export { type GetRiskWarningTextProps, type PresetAlike, getRiskWarningText }
